/*!
 * Orchestration check — page wiring.
 *
 * Hand it a score, get back the list a copyist would hand you — and, for every
 * item on it, the sound that produced it. Everything happens on the notes.
 *
 * Layout is the shared tool-console (rail = what you give it and what it found;
 * stage = the score itself). Three stage views, and which one opens is a
 * decision about what the user needs at that moment: with no file the reference
 * chart, because it answers "what does this thing know"; with a file the
 * findings, because that is what they came for.
 */

import { load, makeTickClock } from '../midi-humanizer/smf.js';
import { loadScriptChain } from '../midi-humanizer/assets.js';
import { check, SEVERITY } from './check.js';
import { INSTRUMENTS, noteName } from './instruments.js';
import { rangeChart, partRangeBar, scoreMap } from './render.js';
import { Audition, flattenNotes } from './audition.js';
import { isScoreFile, importScore, applyNames } from './import.js';

const $ = id => document.getElementById(id);
const T = (k, fb) => {
    const d = window.__OC_I18N || {};
    return d[k] !== undefined ? d[k] : (fb !== undefined ? fb : k);
};
const fill = (tpl, vars) => String(tpl).replace(/:([a-z_]+)/gi, (m, k) =>
    (vars && vars[k] !== undefined) ? String(vars[k]) : m);

const state = {
    doc: null, clock: null, notes: null, result: null,
    fileName: '', fileKey: null, level: 'pro',
    filter: null, partFilter: null, view: 'ranges', playingId: null,
};
const audio = new Audition();

/* ────────────────────────────────────────────────────────────── boot ── */

function boot() {
    const zone = $('ocUpload');
    if (zone && window.__bindUploaderFile) window.__bindUploaderFile(zone, onFile);
    else if (zone) zone.addEventListener('tool-uploader:file', e => e.detail && e.detail.file && onFile(e.detail.file));
    if (zone) zone.addEventListener('tool-uploader:clear', reset);

    document.querySelectorAll('[data-sev]').forEach(b => {
        b.addEventListener('click', () => {
            const sev = b.dataset.sev;
            setFilter(state.filter === sev ? null : sev);
            if (window.__aeFunnel) window.__aeFunnel('filter', { severity: state.filter || 'all' });
        });
    });

    document.querySelectorAll('[data-oclevel]').forEach(b => {
        b.addEventListener('click', () => setLevel(b.dataset.oclevel));
    });

    document.querySelectorAll('[data-ocview]').forEach(b => {
        b.addEventListener('click', () => setView(b.dataset.ocview));
    });

    const dl = $('ocExport'); if (dl) dl.addEventListener('click', exportReport);
    const cp = $('ocCopy');   if (cp) cp.addEventListener('click', copyReport);

    /* A tab that plays sound must stop when it stops being looked at. */
    document.addEventListener('visibilitychange', () => { if (document.hidden) stopAudio(); });
    window.addEventListener('pagehide', stopAudio);

    renderChart();
    if (window.__stage) window.__stage('idle');
}

/* ───────────────────────────────────────────────────────────── input ── */

async function ensureMidiLib() {
    const a = (window.__OC_ASSETS || {}).midiLib;
    await loadScriptChain(Array.isArray(a) ? a : ['/vendor/midi/Midi.min.js'], 'midi-lib', () => !!window.Midi);
    if (!window.Midi) throw new Error('midi_lib_missing');
}

async function onFile(file) {
    if (!file) return;
    stopAudio();
    state.fileName = file.name || 'score.mid';
    /* Stamp the limit key ONCE, from the file as it arrived. Deriving it later
       from a File the tool may have replaced is how a repeat run stops being
       free — see the image-metadata case in CLAUDE.md. */
    state.fileKey = window.__toolLimit ? window.__toolLimit.fileKey(file) : null;
    if (window.__stage) window.__stage('file_picked');

    const score = isScoreFile(file);
    try {
        await ensureMidiLib();

        let buf, names = null, percussion = null;
        if (score) {
            if (window.__toast) window.__toast(T('reading_score', ''), 'info');
            const a = window.__OC_ASSETS || {};
            const imported = await importScore(file, {
                gpToMidiUrl: a.gpToMidi, alphaTabUrl: a.alphaTab, alphaTabFallback: a.alphaTabFallback,
            });
            buf = imported.bytes.buffer ? imported.bytes.buffer : imported.bytes;
            names = imported.names;
            percussion = imported.percussion;
        } else {
            buf = await file.arrayBuffer();
        }

        const doc = load(window.Midi, buf);
        if (names) applyNames(doc, names, percussion);
        if (!doc.tracks.length) throw new Error('no_notes');
        const total = doc.tracks.reduce((s, t) => s + t.notes.length, 0);
        if (!total) throw new Error('no_notes');

        state.doc = doc;
        state.clock = makeTickClock(doc);
        state.notes = flattenNotes(doc, state.clock);

        const t0 = performance.now();
        state.result = check(doc, { level: state.level });
        const ms = Math.round(performance.now() - t0);

        state.filter = null;
        state.partFilter = null;
        document.querySelectorAll('[data-sev]').forEach(x => x.setAttribute('aria-pressed', 'false'));
        renderAll(ms);
        setView('findings');
        if (window.__stage) window.__stage('checked');
        report(ms, score);
    } catch (err) {
        const m = err && err.message;
        const msg = m === 'no_notes' ? T('err_no_notes', '')
            : (score ? T('err_score', '') : T('err_parse', ''));
        if (window.__toast) window.__toast(msg, 'error');
        if (window.__aeDiagnostic) window.__aeDiagnostic({ issue: 'parse_failed', score: score ? 1 : 0, err: String(m) });
    }
}

/* Per-RULE counts are the signal that matters here. Severity totals say how
   loud the tool was; only the rule breakdown says WHICH table is wrong. A rule
   that fires on nearly every score is too strict and is burying the real
   findings; a rule that never fires on real music is dead weight pretending to
   be coverage. Neither is visible without this, and neither would ever arrive
   as a bug report — people just stop trusting the report and leave. */
function report(ms, wasScore) {
    const r = state.result, doc = state.doc;
    const byRule = {};
    r.findings.forEach(f => { byRule[f.rule] = (byRule[f.rule] || 0) + 1; });
    const unknownPrograms = Array.from(new Set(r.uncheckedTracks.map(t => t.program))).slice(0, 8);
    if (!window.__aeFunnel) return;
    window.__aeFunnel('score_checked', {
        ms, level: state.level, input: wasScore ? 'score' : 'midi',
        tracks: doc.tracks.length,
        notes: doc.tracks.reduce((s, t) => s + t.notes.length, 0),
        bars: r.bars, checked: r.checkedTracks.length, unchecked: r.uncheckedTracks.length,
        unknown_programs: unknownPrograms.join(','),
        named_tracks: doc.tracks.filter(t => t.name).length,
        meter_changes: Math.max(0, doc.timeSignatures.length - 1),
        impossible: r.counts.impossible, hard: r.counts.hard, muddy: r.counts.muddy,
        clean: r.findings.length === 0 ? 1 : 0,
        r_range: byRule.range || 0, r_range_extreme: byRule.range_extreme || 0,
        r_breath: byRule.breath || 0, r_divisi: byRule.divisi || 0,
        r_double_stop: byRule.double_stop || 0, r_retune: byRule.retune || 0,
        r_low_interval: byRule.low_interval || 0, r_harp_pedal: byRule.harp_pedal || 0,
        r_harp_speed: byRule.harp_speed || 0, r_stamina: byRule.stamina || 0,
    });
}

function reset() {
    stopAudio();
    state.doc = state.clock = state.notes = state.result = null;
    state.filter = state.partFilter = null;
    ['ocVerdictPanel', 'ocExportPanel'].forEach(id => { const e = $(id); if (e) e.hidden = true; });
    document.querySelectorAll('[data-ocview]').forEach(b => {
        if (b.dataset.ocview !== 'ranges') b.disabled = true;
    });
    setView('ranges');
}

/* ──────────────────────────────────────────────────────────── controls ── */

function setLevel(level) {
    if (state.level === level) return;
    state.level = level;
    document.querySelectorAll('[data-oclevel]').forEach(b => {
        const on = b.dataset.oclevel === level;
        b.classList.toggle('on', on);
        b.setAttribute('aria-pressed', String(on));
    });
    const note = $('ocLevelNote');
    if (note) note.textContent = T('level_note_' + level, '');

    /* The chart is drawn against the level too — the reference and the verdict
       must never disagree about which band is "comfortable". */
    renderChart();
    if (state.doc) {
        state.result = check(state.doc, { level });
        state.partFilter = null;
        renderAll(null);
    }
    if (window.__aeFunnel) window.__aeFunnel('level', { level, loaded: state.doc ? 1 : 0 });
}

function setView(view) {
    state.view = view;
    document.querySelectorAll('[data-ocview]').forEach(b => {
        const on = b.dataset.ocview === view;
        b.classList.toggle('on', on);
        b.setAttribute('aria-selected', String(on));
    });
    const map = { findings: 'ocViewFindings', parts: 'ocViewParts', ranges: 'ocViewRanges' };
    Object.keys(map).forEach(k => { const e = $(map[k]); if (e) e.hidden = k !== view; });
    if (window.__aeFunnel) window.__aeFunnel('view', { view });
}

function setFilter(sev) {
    state.filter = sev;
    document.querySelectorAll('[data-sev]').forEach(x =>
        x.setAttribute('aria-pressed', String(x.dataset.sev === sev)));
    renderFindings();
    if (sev && state.view !== 'findings') setView('findings');
}

/* ──────────────────────────────────────────────────────────── rendering ── */

function renderAll(ms) {
    const r = state.result;

    ['ocVerdictPanel', 'ocExportPanel'].forEach(id => { const e = $(id); if (e) e.hidden = false; });
    document.querySelectorAll('[data-ocview]').forEach(b => { b.disabled = false; });

    SEVERITY.forEach(sev => {
        const el = $('ocCount_' + sev);
        if (el) el.textContent = String(r.counts[sev]);
        const btn = document.querySelector(`[data-sev="${sev}"]`);
        if (btn) btn.hidden = r.counts[sev] === 0;
    });

    const clean = $('ocClean');
    if (clean) clean.hidden = r.findings.length > 0;

    const scale = $('ocScale');
    if (scale) scale.textContent = fill(T('scale_line', ':bars · :parts'), {
        bars: r.bars, parts: r.checkedTracks.length,
        ms: ms == null ? '' : ms,
    });

    /* Which parts were NOT checked, and why. A silent omission would read as
       "everything here is fine". The rail carries the count so it is always in
       view; the parts tab carries the names. */
    const idLine = $('ocIdLine');
    if (idLine) {
        idLine.innerHTML = '';
        const n = r.checkedTracks.length, u = r.uncheckedTracks.length;
        const span = document.createElement('span');
        span.innerHTML = u
            ? fill(T('id_line', ''), { n, u })
            : fill(T('id_line_all', ''), { n });
        idLine.appendChild(span);
        idLine.appendChild(document.createTextNode(' '));
        const link = document.createElement('button');
        link.type = 'button';
        link.textContent = T('see_parts', '');
        link.addEventListener('click', () => setView('parts'));
        idLine.appendChild(link);
    }

    const un = $('ocUnchecked');
    if (un) {
        if (!r.uncheckedTracks.length) un.hidden = true;
        else {
            un.hidden = false;
            un.textContent = T('unchecked', '') + r.uncheckedTracks
                .map(t => (t.name || (T('program', '') + ' ' + t.program))).join(', ');
        }
    }

    renderMap();
    renderParts();
    renderFindings();
}

function instrLabel(key) { return T('instr_' + key, key); }
function famLabel(f) { return T('fam_' + f, f); }

function renderChart() {
    const host = $('ocChart');
    if (!host) return;
    host.innerHTML = '';
    host.appendChild(rangeChart({
        level: state.level,
        labelFor: instrLabel,
        familyLabel: famLabel,
        ariaLabel: T('ranges_title', 'Instrument ranges'),
        /* Clicking an instrument plays its extremes. The chart is a reference
           and a reference you can hear is a better one — and it is the same
           gesture the findings use, so the affordance is learned once. */
        onPick: (key, spec) => {
            audio.playChord([spec.lo, spec.hi], { seconds: 1.4 });
            if (window.__aeFunnel) window.__aeFunnel('hear_instrument', { instrument: key });
        },
    }));
}

function renderMap() {
    const host = $('ocMap');
    if (!host || !state.result) return;
    host.innerHTML = '';
    const el = scoreMap(state.result, {
        labelFor: p => p.name || instrLabel(p.instrument),
        selected: state.partFilter,
        ariaLabel: T('map_title', 'Score map'),
        onPick: (trackIndex) => {
            state.partFilter = state.partFilter === trackIndex ? null : trackIndex;
            renderMap(); renderParts(); renderFindings();
        },
    });
    if (el) {
        /* Row height is one number shared by the HTML label column and the SVG
           cell grid — declared once here so the two can never drift apart. */
        const rowH = Math.max(12, Math.min(18, Math.round(170 / Math.max(1, state.result.checkedTracks.length))));
        el.style.setProperty('--oc-row', rowH + 'px');
        el.style.setProperty('--oc-rows', String(state.result.checkedTracks.length));
        host.appendChild(el);
    }
}

function renderParts() {
    const host = $('ocParts');
    if (!host || !state.result) return;
    host.innerHTML = '';

    const counts = {};
    state.result.findings.forEach(f => { counts[f.track] = (counts[f.track] || 0) + 1; });

    state.result.checkedTracks.forEach(p => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'oc-part';
        btn.setAttribute('aria-pressed', String(state.partFilter === p.index));

        const id = document.createElement('span');
        id.className = 'oc-part-id';
        const nm = document.createElement('span');
        nm.className = 'oc-part-name';
        nm.textContent = p.name || (T('program', '') + ' ' + p.index);
        id.appendChild(nm);
        const as = document.createElement('span');
        /* Say HOW we decided. A name match is the publisher's own word; a
           program match is our inference, and the user is the only person who
           can catch it being wrong. */
        as.className = 'oc-part-as' + (p.source === 'program' ? ' guess' : '');
        as.textContent = instrLabel(p.instrument) + (p.source === 'program' ? ' · ' + T('guessed', '') : '');
        id.appendChild(as);
        btn.appendChild(id);

        btn.appendChild(partRangeBar(p, state.level));

        const nums = document.createElement('span');
        nums.className = 'oc-part-nums';
        if (p.useLo != null) {
            const out = p.useLo < p.spec.lo || p.useHi > p.spec.hi;
            nums.innerHTML = '';
            const rangeTxt = document.createElement(out ? 'b' : 'span');
            if (out) rangeTxt.className = 'oc-part-bad';
            rangeTxt.textContent = noteName(p.useLo) + '–' + noteName(p.useHi);
            nums.appendChild(rangeTxt);
            if (counts[p.index]) {
                nums.appendChild(document.createElement('br'));
                const c = document.createElement('span');
                c.textContent = '⚑ ' + counts[p.index];
                nums.appendChild(c);
            }
        }
        btn.appendChild(nums);

        btn.addEventListener('click', () => {
            state.partFilter = (state.partFilter === p.index) ? null : p.index;
            renderParts(); renderMap(); renderFindings();
            if (window.__aeFunnel) window.__aeFunnel('part_filter', { on: state.partFilter !== null ? 1 : 0, instrument: p.instrument });
        });
        host.appendChild(btn);
    });
}

const RULE_KEY = {
    range: 'r_range', range_extreme: 'r_range_extreme', breath: 'r_breath',
    divisi: 'r_divisi', double_stop: 'r_double_stop', retune: 'r_retune',
    low_interval: 'r_low_interval', harp_pedal: 'r_harp_pedal',
    harp_speed: 'r_harp_speed', stamina: 'r_stamina',
};

const PLAY_ICON = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 2.5v11l9-5.5z"/></svg>';
const STOP_ICON = '<svg viewBox="0 0 16 16" aria-hidden="true"><rect x="3.5" y="3.5" width="9" height="9" rx="1.5"/></svg>';

function renderFindings() {
    const host = $('ocFindings');
    if (!host || !state.result) return;
    host.innerHTML = '';

    let list = state.result.findings;
    if (state.filter) list = list.filter(f => f.severity === state.filter);
    if (state.partFilter !== null) list = list.filter(f => f.track === state.partFilter);

    const hint = $('ocFilterHint');
    if (hint) {
        const on = state.filter || state.partFilter !== null;
        hint.textContent = on
            ? fill(T('filter_on', ''), { n: list.length, total: state.result.findings.length })
            : fill(T('filter_off', ''), { n: state.result.findings.length });
    }

    if (!list.length && state.result.findings.length) {
        const none = document.createElement('p');
        none.className = 'tc-note';
        /* A part with no findings is a RESULT, not an empty screen. Saying
           "nothing matches the filter" there answers a question the user did
           not ask; they asked whether this part is all right. */
        if (state.partFilter !== null && !state.filter) {
            const t = state.result.checkedTracks.find(x => x.index === state.partFilter);
            none.textContent = fill(T('part_clean', ''), { part: (t && t.name) || '' });
        } else {
            none.textContent = T('no_match', '');
        }
        host.appendChild(none);
    }

    list.forEach((f, i) => {
        const id = f.rule + ':' + f.track + ':' + i;
        const row = document.createElement('div');
        row.className = 'oc-item oc-' + f.severity;

        const head = document.createElement('div');
        head.className = 'oc-item-head';
        const sev = document.createElement('span');
        sev.className = 'oc-badge';
        sev.textContent = T('sev_' + f.severity, f.severity);
        head.appendChild(sev);
        const title = document.createElement('span');
        title.className = 'oc-item-title';
        title.textContent = f.trackName || T('untitled', '');
        head.appendChild(title);
        if (f.count > 1) {
            const c = document.createElement('span');
            c.className = 'oc-item-count';
            c.textContent = '×' + f.count;
            head.appendChild(c);
        }
        row.appendChild(head);

        const body = document.createElement('div');
        body.className = 'oc-item-body';
        body.textContent = describe(f);
        row.appendChild(body);

        if (f.places && f.places.length) {
            const where = document.createElement('div');
            where.className = 'oc-item-where';
            where.textContent = T('where', '') + ' ' + f.places.map(p => p.bar).join(', ')
                + (f.count > f.places.length ? ' …' : '');
            row.appendChild(where);
        }

        const play = playButton(f, id, row);
        if (play) row.appendChild(play);
        host.appendChild(row);
    });
}

/**
 * The button that ends the argument.
 *
 * Only offered where there is something to hear: a place in the score, or a
 * pair of pitches. A stamina note has neither a moment nor a sound, and a play
 * button that plays nothing teaches people to stop pressing them.
 */
function playButton(f, id, row) {
    if (!state.notes || !f.places || !f.places.length) return null;
    const at = f.places[0];
    if (at.sec == null) return null;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'oc-play';
    btn.innerHTML = PLAY_ICON;
    btn.title = T('play', '');
    btn.setAttribute('aria-label', T('play', ''));

    btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        if (state.playingId === id) { stopAudio(); return; }
        stopAudio();
        state.playingId = id;
        btn.innerHTML = STOP_ICON;
        btn.classList.add('playing');
        /* A toggle whose accessible name never changes tells a screen-reader
           user the button still says "play" while it is playing. */
        btn.title = T('stop', '');
        btn.setAttribute('aria-label', T('stop', ''));
        row.classList.add('on');

        const done = () => {
            if (state.playingId !== id) return;
            state.playingId = null;
            btn.innerHTML = PLAY_ICON;
            btn.classList.remove('playing');
            btn.title = T('play', '');
            btn.setAttribute('aria-label', T('play', ''));
            row.classList.remove('on');
        };

        /* A low-interval finding is ABOUT two pitches, so play those two alone
           first — in context they are buried under the tutti that made them
           hard to notice in the first place. Everything else is about a moment
           in the score, so play the moment. */
        let ok;
        if (f.rule === 'low_interval' && at.midi != null && at.midi2 != null) {
            ok = await audio.playChord([at.midi, at.midi2], { seconds: 2.2, onEnd: done });
        } else {
            const from = Math.max(0, at.sec - 0.4);
            ok = await audio.playWindow(state.notes, from, from + 5, {
                onlyTracks: f.rule === 'stamina' || f.rule === 'breath' ? [f.track] : null,
                onEnd: done,
            });
        }
        if (!ok) done();
        if (window.__aeFunnel) window.__aeFunnel('hear_finding', { rule: f.rule, severity: f.severity, ok: ok ? 1 : 0 });
    });
    return btn;
}

function stopAudio() {
    audio.stop();
    state.playingId = null;
    document.querySelectorAll('.oc-play.playing').forEach(b => {
        b.innerHTML = PLAY_ICON;
        b.classList.remove('playing');
        b.title = T('play', '');
        b.setAttribute('aria-label', T('play', ''));
    });
    document.querySelectorAll('.oc-item.on').forEach(r => r.classList.remove('on'));
}

/** One sentence a musician can act on, built from the finding's own numbers. */
function describe(f) {
    const d = f.detail || {};
    return fill(T(RULE_KEY[f.rule] || f.rule, f.rule), {
        limit: d.limit || '', worst: d.worst || '',
        seconds: d.seconds !== undefined ? d.seconds : '',
        minutes: d.minutes !== undefined ? d.minutes : '',
        rest: d.rest !== undefined ? d.rest : '',
        pair: d.pair || '', interval: d.interval !== undefined ? d.interval : '',
        min: d.min !== undefined ? d.min : '', voices: d.voices !== undefined ? d.voices : '',
        max: d.max !== undefined ? d.max : '', pedals: d.pedals !== undefined ? d.pedals : '',
        classes: d.classes !== undefined ? d.classes : '', pitches: d.pitches || '',
    });
}

/* ───────────────────────────────────────────────────────────── report ── */

/** A plain-text report, because this is something people paste into an email. */
function exportReport() {
    if (!state.result) return;
    const run = window.__toolLimit
        ? window.__toolLimit.begin('orchestration-check', state.fileKey ? [state.fileKey] : null)
        : { commit() {}, abort() {} };
    if (!run) return;
    try {
        const blob = new Blob([buildReportText()], { type: 'text/plain;charset=utf-8' });
        window.__downloadFile(blob, state.fileName.replace(/\.[a-z0-9]+$/i, '') + ' — orchestration.txt');
        run.commit();
        const r = state.result;
        if (window.__aeToolComplete) window.__aeToolComplete({
            output: 'txt', findings: r.findings.length, level: state.level,
            impossible: r.counts.impossible, hard: r.counts.hard, muddy: r.counts.muddy,
            checked: r.checkedTracks.length, unchecked: r.uncheckedTracks.length,
            filtered: state.filter || 'all',
        });
    } catch (err) {
        run.abort();
        if (window.__handleError) window.__handleError(err, 'orchestration-check/export');
    }
}

/** One text, two buttons — download and copy must never drift apart. */
function buildReportText() {
    const r = state.result;
    const lines = [];
    lines.push(state.fileName);
    lines.push(fill(T('scale_line', ''), { bars: r.bars, parts: r.checkedTracks.length, ms: '' }));
    lines.push(T('level_note_' + state.level, ''));
    /* Name what was NOT checked here too: a report forwarded to a conductor
       must not look more complete than it is. */
    if (r.uncheckedTracks.length) {
        lines.push(T('unchecked', '') + r.uncheckedTracks.map(t => t.name || ('#' + t.index)).join(', '));
    }
    lines.push('');
    SEVERITY.forEach(sev => {
        const items = r.findings.filter(f => f.severity === sev);
        if (!items.length) return;
        lines.push('== ' + T('sev_' + sev, sev) + ' (' + items.length + ') ==');
        items.forEach(f => {
            lines.push('- ' + (f.trackName || '?') + (f.count > 1 ? ' ×' + f.count : '')
                + ' — ' + describe(f)
                + (f.places && f.places.length ? ' [' + T('where', '') + ' ' + f.places.map(p => p.bar).join(', ') + ']' : ''));
        });
        lines.push('');
    });
    if (!r.findings.length) lines.push(T('clean', ''));
    return lines.join('\n');
}

/* Most of these reports go into a message to a conductor, not into a folder.
   Saving a file to then open and copy it is three steps for the common case. */
async function copyReport() {
    if (!state.result) return;
    const btn = $('ocCopy');
    try {
        await navigator.clipboard.writeText(buildReportText());
        if (btn) {
            const was = btn.textContent;
            btn.textContent = T('copied', '');
            setTimeout(() => { btn.textContent = was; }, 1600);
        }
        if (window.__aeFunnel) window.__aeFunnel('copy_report', { findings: state.result.findings.length });
    } catch (err) {
        if (window.__toast) window.__toast(T('copy_failed', ''), 'warning');
    }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();

window.__OC = { state, onFile, setLevel, setView };
